import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  Eye,
  HandshakeIcon,
  Loader2,
  MessageSquare,
  Package,
  Search,
  Store,
  UserRound,
  X,
} from "lucide-react";

import { integrationsList, productsList } from "../../../../api/auth";
import { createCollaboration, getAssignmentCoverage } from "../../../../api/marketplace";
import { makePublicEntity, usePublicEntities } from "../../../../hooks/usePublicEntities";
import {
  EmptyState,
  FeedbackMessage,
  getApiErrorMessage,
  LoadingState,
} from "../../../ui/Feedback";
import { useOffers } from "../hooks/useOffers";
import { chatLink } from "../utils/chatLink";

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-indigo-500/30";

const assignmentModes = [
  {
    value: "SOURCE",
    label: "Boutique complete",
    description: "Toutes les commandes des sources selectionnees",
    icon: Store,
  },
  {
    value: "PRODUCT",
    label: "Produits",
    description: "Uniquement les produits selectionnes",
    icon: Package,
  },
  {
    value: "MANUAL",
    label: "Manuelle",
    description: "Affectation commande par commande",
    icon: HandshakeIcon,
  },
];

const statusLabels = {
  active: "Active",
  inactive: "Inactive",
  archived: "Archivee",
};

const statusStyles = {
  active:
    "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400",
  inactive:
    "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400",
  archived:
    "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400",
};

const responseList = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const percentage = (value) =>
  value === null || value === undefined
    ? null
    : `${Math.round(Number(value) * 100)}%`;

const providerFallback = (type) =>
  type === "AGENCY_OWNER" ? "Agence de confirmation" : "Agent de confirmation";

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center rounded-lg border px-2.5 py-1 text-xs font-medium ${
        statusStyles[status] || statusStyles.inactive
      }`}
    >
      {statusLabels[status] || status || "Inconnue"}
    </span>
  );
}

function ProviderIdentity({ offer, profile, compact = false }) {
  const ProviderIcon = offer.provider_type === "AGENCY_OWNER" ? Building2 : UserRound;
  const name = profile?.display_name || providerFallback(offer.provider_type);
  const subtitle = profile?.subtitle || providerFallback(offer.provider_type);

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {profile?.avatar ? (
        <img
          src={profile.avatar}
          alt=""
          className={`${compact ? "h-8 w-8" : "h-10 w-10"} shrink-0 rounded-lg object-cover`}
        />
      ) : (
        <span
          className={`${compact ? "h-8 w-8" : "h-10 w-10"} flex shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400`}
        >
          <ProviderIcon size={compact ? 16 : 19} />
        </span>
      )}
      <span className="min-w-0">
        <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
          {name}
        </span>
        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
          {subtitle}
        </span>
      </span>
    </div>
  );
}

function PerformanceSummary({ stats, compact = false }) {
  const confirmationRate = percentage(stats?.confirmation_rate);
  const deliveryRate = percentage(stats?.final_delivery_success_rate);

  if (!stats?.orders_received_tracked) {
    return (
      <span className="inline-flex rounded-lg border border-dashed border-sky-300 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700 dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-300">
        Nouveau profil
      </span>
    );
  }

  return (
    <div className={`grid ${compact ? "grid-cols-2" : "grid-cols-3"} gap-2 text-xs`}>
      <div>
        <p className="text-slate-500 dark:text-slate-400">Confirmation</p>
        <p className="font-semibold text-slate-900 dark:text-slate-100">
          {confirmationRate || "-"}
        </p>
      </div>
      <div>
        <p className="text-slate-500 dark:text-slate-400">Livraison</p>
        <p className="font-semibold text-slate-900 dark:text-slate-100">
          {deliveryRate || "-"}
        </p>
      </div>
      {!compact ? (
        <div>
          <p className="text-slate-500 dark:text-slate-400">Commandes</p>
          <p className="font-semibold text-slate-900 dark:text-slate-100">
            {stats.orders_received_tracked}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function OfferViewModal({ open, onClose, offer, profile }) {
  if (!open || !offer) return null;
  const stats = offer.provider_stats || {};
  const publicProfile = profile?.profile || {};
  const location = [publicProfile.city, publicProfile.country].filter(Boolean).join(", ");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 p-5 dark:border-slate-800">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Details de l'offre
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            aria-label="Fermer"
            title="Fermer"
          >
            <X size={19} />
          </button>
        </div>

        <div className="space-y-5 p-5">
          <ProviderIdentity offer={offer} profile={profile} />

          <div>
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Offre</p>
            <p className="mt-1 font-semibold text-slate-900 dark:text-slate-100">{offer.titre}</p>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-600 dark:text-slate-300">
              {offer.description || "Aucune description."}
            </p>
          </div>

          <div className="grid gap-4 border-y border-slate-200 py-4 sm:grid-cols-3 dark:border-slate-800">
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Prix</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                {offer.prix} {offer.currency}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Statut</p>
              <div className="mt-1"><StatusBadge status={offer.status} /></div>
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Localisation</p>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                {location || "Non renseignee"}
              </p>
            </div>
          </div>

          <div>
            <p className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
              Performance verifiee par Teqa
            </p>
            <PerformanceSummary stats={stats} />
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                Note {stats.store_review_average || 0}/5 ({stats.store_reviews_count || 0} avis)
              </span>
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                {stats.active_collaborations || 0} collaboration(s) active(s)
              </span>
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 dark:bg-slate-800">
                {stats.paid_orders || 0} commission(s) reglee(s)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResourceEmpty({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center px-4 py-8 text-center">
      <Icon size={25} className="mb-2 text-slate-400" />
      <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</p>
      <p className="mt-1 max-w-sm text-xs leading-5 text-slate-500 dark:text-slate-400">
        {message}
      </p>
    </div>
  );
}

const EMPTY_COVERAGE = { sources: {}, products: {}, sources_with_product_assignments: [] };

function RequestCollaborationModal({ open, onClose, offer }) {
  const [form, setForm] = useState({
    price_finale: "",
    message: "",
    max_orders_per_day: "",
    max_orders_total: "",
    expires_at: "",
    assignment_type: "SOURCE",
    selected_source_ids: [],
    selected_product_ids: [],
  });
  const [sources, setSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [resourceSearch, setResourceSearch] = useState("");
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [resourcesError, setResourcesError] = useState(null);
  const [coverage, setCoverage] = useState(EMPTY_COVERAGE);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;

    setForm({
      price_finale: offer?.prix || "",
      message: "",
      max_orders_per_day: "",
      max_orders_total: "",
      expires_at: "",
      assignment_type: "SOURCE",
      selected_source_ids: [],
      selected_product_ids: [],
    });
    setResourceSearch("");
    setSuccess(false);
    setError(null);
    setResourcesError(null);
    setResourcesLoading(true);

    Promise.all([
      integrationsList(),
      productsList(),
      getAssignmentCoverage().catch(() => ({ data: null })),
    ])
      .then(([sourceResponse, productResponse, coverageResponse]) => {
        if (cancelled) return;
        setSources(responseList(sourceResponse).filter((source) => source.is_active));
        setProducts(responseList(productResponse));
        setCoverage(coverageResponse?.data || EMPTY_COVERAGE);
      })
      .catch((requestError) => {
        if (!cancelled) setResourcesError(requestError);
      })
      .finally(() => {
        if (!cancelled) setResourcesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, offer]);

  const sourceNames = useMemo(
    () => Object.fromEntries(sources.map((source) => [String(source.id), source.store_name])),
    [sources]
  );
  const activeSourceIds = useMemo(
    () => new Set(sources.map((source) => String(source.id))),
    [sources]
  );
  const eligibleProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          product.source_connection &&
          product.external_product_id &&
          activeSourceIds.has(String(product.source_connection))
      ),
    [activeSourceIds, products]
  );
  const visibleProducts = useMemo(() => {
    const query = resourceSearch.trim().toLowerCase();
    if (!query) return eligibleProducts;
    return eligibleProducts.filter((product) =>
      [product.title, sourceNames[String(product.source_connection)], product.external_product_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [eligibleProducts, resourceSearch, sourceNames]);

  // Règles d'affectation : ce qui est déjà confié ne peut pas l'être une 2e fois
  const coverageOwners = useMemo(
    () =>
      [...Object.values(coverage.sources || {}), ...Object.values(coverage.products || {})]
        .map((entry) => makePublicEntity(entry.provider_type, entry.provider_id))
        .filter(Boolean),
    [coverage]
  );
  const { getEntity: getOwner } = usePublicEntities(coverageOwners);
  const ownerName = (entry) =>
    getOwner(entry.provider_type, entry.provider_id)?.display_name ||
    (entry.provider_type === "AGENCY_OWNER" ? "une agence" : "un agent");

  const sourceLock = (sourceId) => {
    const entry = coverage.sources?.[sourceId];
    if (entry?.blocking) return { blocked: true, text: `Déjà confiée entièrement à ${ownerName(entry)}` };
    if ((coverage.sources_with_product_assignments || []).includes(sourceId)) {
      return { blocked: true, text: "Des produits de cette boutique sont déjà confiés : impossible de la confier en entier" };
    }
    if (entry) return { blocked: false, text: `Demande en attente avec ${ownerName(entry)}` };
    return { blocked: false, text: "" };
  };

  const productLock = (product) => {
    const sourceId = String(product.source_connection);
    const sourceEntry = coverage.sources?.[sourceId];
    if (sourceEntry?.blocking) return { blocked: true, text: `Boutique confiée entièrement à ${ownerName(sourceEntry)}` };
    const entry = coverage.products?.[`${sourceId}|${product.external_product_id}`];
    if (entry?.blocking) return { blocked: true, text: `Déjà confié à ${ownerName(entry)}` };
    if (entry) return { blocked: false, text: `Demande en attente avec ${ownerName(entry)}` };
    return { blocked: false, text: "" };
  };

  if (!open || !offer) return null;

  const toggleSelection = (field, id) => {
    const normalized = String(id);
    setForm((previous) => ({
      ...previous,
      [field]: previous[field].includes(normalized)
        ? previous[field].filter((value) => value !== normalized)
        : [...previous[field], normalized],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (form.assignment_type === "SOURCE" && !form.selected_source_ids.length) {
      setError("Selectionnez au moins une boutique connectee.");
      return;
    }
    if (form.assignment_type === "PRODUCT" && !form.selected_product_ids.length) {
      setError("Selectionnez au moins un produit importe.");
      return;
    }

    const selectedProducts = eligibleProducts.filter((product) =>
      form.selected_product_ids.includes(String(product.id))
    );
    const productRules = selectedProducts.map((product) => ({
      source_connection_id: String(product.source_connection),
      external_product_id: String(product.external_product_id),
      integration_product_id: String(product.id),
      // libellés affichés à l'agence (au lieu des identifiants)
      title: product.title || "",
      image_url: product.image_url || "",
    }));

    const payload = {
      provider_type: offer.provider_type,
      provider_id: offer.provider_id,
      offer: offer.id,
      price_finale: Number(form.price_finale),
      currency: "MAD",
      assignment_type: form.assignment_type,
    };
    if (form.assignment_type === "SOURCE") {
      payload.source_ids = form.selected_source_ids;
    }
    if (form.assignment_type === "PRODUCT") {
      payload.source_ids = [...new Set(productRules.map((rule) => rule.source_connection_id))];
      payload.product_rules = productRules;
    }
    // nom + plateforme de chaque boutique concernée, pour l'agence de confirmation
    payload.source_labels = Object.fromEntries(
      sources
        .filter((source) => (payload.source_ids || []).includes(String(source.id)))
        .map((source) => [String(source.id), { name: source.store_name || "", platform: source.platform || "" }])
    );
    if (form.message.trim()) payload.message = form.message.trim();
    if (form.max_orders_per_day) payload.max_orders_per_day = Number(form.max_orders_per_day);
    if (form.max_orders_total) payload.max_orders_total = Number(form.max_orders_total);
    if (form.expires_at) payload.expires_at = form.expires_at;

    setSubmitting(true);
    try {
      await createCollaboration(payload);
      setSuccess(true);
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          "La demande de collaboration n'a pas pu etre envoyee."
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
      <div className="max-h-[94vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 p-5 dark:border-slate-800">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              Demander une collaboration
            </h2>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{offer.titre}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            aria-label="Fermer"
            title="Fermer"
          >
            <X size={19} />
          </button>
        </div>

        {success ? (
          <div className="px-6 py-12 text-center">
            <CheckCircle2 size={42} className="mx-auto mb-3 text-emerald-500" />
            <p className="font-semibold text-slate-900 dark:text-slate-100">Demande envoyee</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Le prestataire peut maintenant accepter ou negocier votre demande.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 p-5">
            {error ? (
              <FeedbackMessage type="error" title="Collaboration impossible" message={error} />
            ) : null}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Prix par commande (MAD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={form.price_finale}
                  onChange={(event) => setForm({ ...form, price_finale: event.target.value })}
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Date de fin
                </label>
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(event) => setForm({ ...form, expires_at: event.target.value })}
                  className={inputClass}
                />
              </div>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
                Mode d'affectation
              </legend>
              <div className="grid gap-2 md:grid-cols-3">
                {assignmentModes.map((mode) => {
                  const Icon = mode.icon;
                  const selected = form.assignment_type === mode.value;
                  return (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() =>
                        setForm((previous) => ({ ...previous, assignment_type: mode.value }))
                      }
                      className={`flex min-h-20 items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
                        selected
                          ? "border-indigo-500 bg-indigo-50 text-indigo-800 dark:bg-indigo-500/10 dark:text-indigo-300"
                          : "border-slate-200 text-slate-700 hover:border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:border-slate-600"
                      }`}
                    >
                      <Icon size={18} className="mt-0.5 shrink-0" />
                      <span>
                        <span className="block text-sm font-semibold">{mode.label}</span>
                        <span className="mt-0.5 block text-xs leading-4 opacity-75">
                          {mode.description}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            {form.assignment_type !== "MANUAL" ? (
              <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-700 dark:bg-slate-800/60">
                  <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                    {form.assignment_type === "SOURCE"
                      ? `Boutiques selectionnees (${form.selected_source_ids.length})`
                      : `Produits selectionnes (${form.selected_product_ids.length})`}
                  </p>
                  {form.assignment_type === "PRODUCT" && eligibleProducts.length ? (
                    <div className="relative w-52 max-w-[50%]">
                      <Search
                        size={15}
                        className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        value={resourceSearch}
                        onChange={(event) => setResourceSearch(event.target.value)}
                        placeholder="Rechercher"
                        className="w-full rounded-lg border border-slate-300 bg-white py-1.5 pl-8 pr-2 text-xs outline-none focus:border-indigo-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                      />
                    </div>
                  ) : null}
                </div>

                {resourcesLoading ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                    <Loader2 size={18} className="animate-spin" /> Chargement du catalogue...
                  </div>
                ) : resourcesError ? (
                  <div className="p-4">
                    <FeedbackMessage
                      type="error"
                      title="Catalogue indisponible"
                      message={getApiErrorMessage(resourcesError)}
                    />
                  </div>
                ) : form.assignment_type === "SOURCE" ? (
                  sources.length ? (
                    <div className="max-h-64 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
                      {sources.map((source) => {
                        const id = String(source.id);
                        const selected = form.selected_source_ids.includes(id);
                        const lock = sourceLock(id);
                        return (
                          <label
                            key={id}
                            title={lock.text}
                            className={`flex items-center gap-3 px-4 py-3 ${
                              lock.blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                            } ${
                              selected
                                ? "bg-indigo-50 dark:bg-indigo-500/10"
                                : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selected}
                              disabled={lock.blocked && !selected}
                              onChange={() => toggleSelection("selected_source_ids", id)}
                              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <Store size={18} className="text-slate-400" />
                            <span className="min-w-0">
                              <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                                {source.store_name || "Boutique sans nom"}
                              </span>
                              <span className="text-xs uppercase text-slate-500 dark:text-slate-400">
                                {source.platform}
                              </span>
                              {lock.text ? (
                                <span className={`block text-xs ${lock.blocked ? "text-rose-600" : "text-amber-600"}`}>
                                  {lock.text}
                                </span>
                              ) : null}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <ResourceEmpty
                      icon={Store}
                      title="Aucune boutique active"
                      message="Connectez d'abord une boutique Shopify ou YouCan dans Integrations."
                    />
                  )
                ) : visibleProducts.length ? (
                  <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto dark:divide-slate-800">
                    {visibleProducts.map((product) => {
                      const id = String(product.id);
                      const selected = form.selected_product_ids.includes(id);
                      const lock = productLock(product);
                      return (
                        <label
                          key={id}
                          title={lock.text}
                          className={`flex items-center gap-3 px-4 py-3 ${
                            lock.blocked ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                          } ${
                            selected
                              ? "bg-indigo-50 dark:bg-indigo-500/10"
                              : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={lock.blocked && !selected}
                            onChange={() => toggleSelection("selected_product_ids", id)}
                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          {product.image_url ? (
                            <img
                              src={product.image_url}
                              alt=""
                              className="h-10 w-10 shrink-0 rounded-lg object-cover"
                            />
                          ) : (
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400 dark:bg-slate-800">
                              <Package size={18} />
                            </span>
                          )}
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                              {product.title}
                            </span>
                            <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                              {sourceNames[String(product.source_connection)] || product.platform}
                            </span>
                            {lock.text ? (
                              <span className={`block truncate text-xs ${lock.blocked ? "text-rose-600" : "text-amber-600"}`}>
                                {lock.text}
                              </span>
                            ) : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <ResourceEmpty
                    icon={Package}
                    title={resourceSearch ? "Aucun resultat" : "Aucun produit importe"}
                    message={
                      resourceSearch
                        ? "Modifiez votre recherche pour retrouver un produit."
                        : "Importez les produits de vos boutiques dans le catalogue avant de les affecter."
                    }
                  />
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300">
                Les commandes seront affectees individuellement depuis la liste des commandes.
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Maximum par jour
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.max_orders_per_day}
                  onChange={(event) =>
                    setForm({ ...form, max_orders_per_day: event.target.value })
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                  Maximum total
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.max_orders_total}
                  onChange={(event) =>
                    setForm({ ...form, max_orders_total: event.target.value })
                  }
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                Message au prestataire
              </label>
              <textarea
                rows={3}
                value={form.message}
                onChange={(event) => setForm({ ...form, message: event.target.value })}
                placeholder="Conditions ou informations utiles"
                className={inputClass}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end dark:border-slate-800">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={submitting || resourcesLoading}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <HandshakeIcon size={16} />}
                {submitting ? "Envoi..." : "Envoyer la demande"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function OfferRow({ offer, profile, onView, onRequest, onContact }) {
  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className="px-4 py-3">
        <p className="font-medium text-slate-900 dark:text-slate-100">{offer.titre}</p>
        <p className="max-w-xs truncate text-xs text-slate-500 dark:text-slate-400">
          {offer.description || "Aucune description"}
        </p>
      </td>
      <td className="px-4 py-3"><ProviderIdentity offer={offer} profile={profile} compact /></td>
      <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
        {offer.prix} {offer.currency}
      </td>
      <td className="px-4 py-3"><PerformanceSummary stats={offer.provider_stats} compact /></td>
      <td className="px-4 py-3"><StatusBadge status={offer.status} /></td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onView}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
            title="Voir les details"
            aria-label="Voir les details"
          >
            <Eye size={17} />
          </button>
          <button
            type="button"
            onClick={onContact}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
            title="Contacter le prestataire"
            aria-label="Contacter le prestataire"
          >
            <MessageSquare size={17} />
          </button>
          <button
            type="button"
            onClick={onRequest}
            className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700"
          >
            <HandshakeIcon size={15} /> Demander
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminMarketplaceOffers() {
  const { offers, loading, error, fetchOffers } = useOffers();
  const navigate = useNavigate();
  const contact = (offer) =>
    navigate(
      chatLink({
        type: offer.provider_type,
        id: offer.provider_id,
        subject: `Offre : ${offer.titre}`,
        offerId: offer.id,
      })
    );
  const [search, setSearch] = useState("");
  const [viewModal, setViewModal] = useState({ open: false, offer: null });
  const [collabModal, setCollabModal] = useState({ open: false, offer: null });
  const providerLookups = useMemo(
    () =>
      offers
        .map((offer) =>
          offer.provider_lookup || makePublicEntity(offer.provider_type, offer.provider_id)
        )
        .filter(Boolean),
    [offers]
  );
  const { getEntity } = usePublicEntities(providerLookups);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return offers;
    return offers.filter((offer) => {
      const profile = getEntity(offer.provider_type, offer.provider_id);
      return [offer.titre, offer.description, profile?.display_name, profile?.subtitle]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [getEntity, offers, search]);

  if (loading) return <LoadingState label="Chargement des offres..." />;

  if (error) {
    return (
      <div className="space-y-3">
        <FeedbackMessage
          type="error"
          title="Offres indisponibles"
          message={getApiErrorMessage(error, "Les offres n'ont pas pu etre chargees.")}
        />
        <button
          type="button"
          onClick={() => fetchOffers()}
          className="rounded-lg bg-rose-600 px-4 py-2 text-sm text-white hover:bg-rose-700"
        >
          Reessayer
        </button>
      </div>
    );
  }

  const selectedProfile = viewModal.offer
    ? getEntity(viewModal.offer.provider_type, viewModal.offer.provider_id)
    : null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Offres du marketplace
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Comparez les prestataires et choisissez comment leur affecter vos commandes.
        </p>
      </div>

      <div className="relative max-w-xl">
        <Search
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          type="search"
          placeholder="Rechercher une offre ou un prestataire"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={`${inputClass} pl-10`}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title={search ? "Aucun resultat" : "Aucune offre disponible"}
          message={
            search
              ? "Modifiez votre recherche pour afficher d'autres prestataires."
              : "Les offres actives des agents et agences apparaitront ici avec leurs statistiques verifiees."
          }
        />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:block dark:border-slate-800 dark:bg-slate-900">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Offre</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Prestataire</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Prix</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Performance</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-700 dark:text-slate-300">Statut</th>
                    <th className="px-4 py-3 text-right font-semibold text-slate-700 dark:text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filtered.map((offer) => (
                    <OfferRow
                      key={offer.id}
                      offer={offer}
                      profile={getEntity(offer.provider_type, offer.provider_id)}
                      onView={() => setViewModal({ open: true, offer })}
                      onRequest={() => setCollabModal({ open: true, offer })}
                      onContact={() => contact(offer)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid gap-3 md:hidden">
            {filtered.map((offer) => (
              <article
                key={offer.id}
                className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              >
                <ProviderIdentity
                  offer={offer}
                  profile={getEntity(offer.provider_type, offer.provider_id)}
                />
                <div className="mt-4">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">{offer.titre}</p>
                  <p className="mt-1 line-clamp-2 text-sm text-slate-500 dark:text-slate-400">
                    {offer.description || "Aucune description"}
                  </p>
                </div>
                <div className="my-4 border-y border-slate-100 py-3 dark:border-slate-800">
                  <PerformanceSummary stats={offer.provider_stats} compact />
                </div>
                <div className="flex items-center justify-between gap-3">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">
                    {offer.prix} {offer.currency}
                  </p>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setViewModal({ open: true, offer })}
                      className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                      title="Voir les details"
                      aria-label="Voir les details"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => contact(offer)}
                      className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
                      title="Contacter le prestataire"
                      aria-label="Contacter le prestataire"
                    >
                      <MessageSquare size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setCollabModal({ open: true, offer })}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-medium text-white hover:bg-indigo-700"
                    >
                      <HandshakeIcon size={15} /> Demander
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      <OfferViewModal
        open={viewModal.open}
        onClose={() => setViewModal({ open: false, offer: null })}
        offer={viewModal.offer}
        profile={selectedProfile}
      />
      <RequestCollaborationModal
        open={collabModal.open}
        onClose={() => setCollabModal({ open: false, offer: null })}
        offer={collabModal.offer}
      />
    </div>
  );
}
